import React from 'react';
import type { useApplicationDetail } from '../../hooks/useApplicationDetail';

interface Props { h: ReturnType<typeof useApplicationDetail>; }

export const EducationEditSection: React.FC<Props> = ({ h }) => {
  const { editedEducation } = h;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center">
          Wykształcenie
          <span className="ml-3 text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Zarządzanie</span>
        </h2>
        <button onClick={h.handleAddEducation} className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center text-sm">
          + Dodaj szkołę
        </button>
      </div>
      <div className="space-y-4">
        {editedEducation.length === 0 ? (
          <p className="text-gray-500 italic">Brak wpisów. Kliknij "Dodaj szkołę" aby dodać wykształcenie.</p>
        ) : (
          editedEducation.map((edu, index) => (
            <div key={index} className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-700">Szkoła #{index + 1}</h3>
                <button onClick={() => h.handleRemoveEducation(index)} className="text-red-600 hover:text-red-800 font-medium text-sm">Usuń</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nazwa szkoły</label>
                  <input type="text" value={edu.school_name || ''} onChange={(e) => h.handleEducationChange(index, 'school_name', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="np. Uniwersytet Jagielloński" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Poziom wykształcenia</label>
                  <select value={edu.education_level || ''} onChange={(e) => h.handleEducationChange(index, 'education_level', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="">Wybierz...</option>
                    <option value="podstawowe">Podstawowe</option>
                    <option value="gimnazjalne">Gimnazjalne</option>
                    <option value="zawodowe">Zawodowe</option>
                    <option value="srednie">Średnie</option>
                    <option value="licencjat">Licencjat</option>
                    <option value="inzynier">Inżynier</option>
                    <option value="magister">Magister</option>
                    <option value="doktor">Doktor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kierunek</label>
                  <input type="text" value={edu.field_of_study || ''} onChange={(e) => h.handleEducationChange(index, 'field_of_study', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="np. Informatyka" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rok ukończenia</label>
                  <input type="number" value={edu.completion_year || ''} onChange={(e) => h.handleEducationChange(index, 'completion_year', parseInt(e.target.value))} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" min="1950" max={new Date().getFullYear()} placeholder="YYYY" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const LEVEL_LABELS: Record<string, string> = {
  podstawowe: 'Podstawowe',
  gimnazjalne: 'Gimnazjalne',
  zawodowe: 'Zawodowe',
  srednie: 'Średnie',
  licencjat: 'Licencjat',
  inzynier: 'Inżynier',
  magister: 'Magister',
  doktor: 'Doktor',
};

export const EducationViewSection: React.FC<Props> = ({ h }) => {
  const { application } = h;
  const education = application?.education || [];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center">
          Wykształcenie
          <span className="ml-3 text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{education.length}</span>
        </h2>
      </div>
      {education.length === 0 ? (
        <p className="text-gray-400 italic text-sm">Brak wpisów o wykształceniu. Klient może je dodać przez portal lub operator w trybie edycji.</p>
      ) : (
        <div className="space-y-3">
          {education.map((edu: any, idx: number) => (
            <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50/30 rounded-r-lg">
              <p className="font-semibold text-gray-900">{edu.school_name || 'Bez nazwy'}</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {edu.education_level && (
                  <span className="inline-block text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {LEVEL_LABELS[edu.education_level] || edu.education_level}
                  </span>
                )}
                {edu.field_of_study && (
                  <span className="inline-block text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    {edu.field_of_study}
                  </span>
                )}
                {edu.completion_year && (
                  <span className="inline-block text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                    {edu.completion_year}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
